// ─────────────────────────────────────────────────────────────────────────────
// ButtonCompo.jsx
//
// Native <button> — no MUI, no variant logic fighting your styles.
// Whatever you pass in `style` or `sx` renders exactly as-is.
//
// Props:
//   name        {string}    — button label (used if no children)
//   children    {node}      — icon / custom content
//   onClick     {function}  — click handler
//   type        {string}    — "button" | "submit" | "reset" (default: "button")
//   disable     {boolean}   — disables the button
//   fullWidth   {boolean}   — width: 100%
//   style       {object}    — inline styles applied directly
//   sx          {object}    — alias for style, merged after
//   className   {string}    — extra CSS class
// ─────────────────────────────────────────────────────────────────────────────

// Base reset — only resets browser defaults, nothing opinionated.
// Your `style` / `sx` always wins on top of this.
const BASE = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    border: "none",
    background: "none",
    padding: 0,
    margin: 0,
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    color: "inherit",
    cursor: "pointer",
    outline: "none",
    boxSizing: "border-box",
    textDecoration: "none",
    transition: "background .15s, color .15s, opacity .15s",
};

const BASE_DISABLED = {
    cursor: "not-allowed",
    opacity: 0.5,
};

export const ButtonComponent = ({
    name,
    children,
    onClick,
    type = "button",
    disable = false,
    fullWidth = false,
    style,
    sx,
    className,
    ...rest           // aria-label, data-*, onMouseEnter, etc.
}) => {
    const mergedStyle = {
        ...BASE,
        ...(disable ? BASE_DISABLED : {}),
        ...(fullWidth ? { width: "100%" } : {}),
        ...(style || {}),   // your style applied directly
        ...(sx || {}),   // sx alias applied on top
    };

    return (
        <button
            type={type}
            disabled={disable}
            className={className}
            style={mergedStyle}
            onClick={disable ? undefined : onClick}
            {...rest}
        >
            {children ?? name}
        </button>
    );
};

// Keep defaultProps for any legacy usage that relies on them
ButtonComponent.defaultProps = {
    type: "button",
};

export default ButtonComponent;